import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ChevronDown, ChevronUp, ExternalLink, Info, Check } from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

// Suites a client may request. TalentIntelligence is excluded by product
// decision (Sprint Preparo Phase 2); TalentTrain is requested as a suite
// only — modes are chosen later by the CTH Advisor during configuration.
const EXCLUDED_SUITES = ['talentintelligence', 'intelligence'];

const STEPS = ['suite', 'collaborators', 'employment', 'review'];

const SUITE_NAMES = {
  intelligence: 'TalentIntelligence',
  climara: 'Climara',
  talentmap: 'TalentMap',
  talentcheck: 'TalentCheck',
  talentstart: 'TalentStart',
  talentheart: 'TalentHeart',
  talentfix: 'TalentFix',
  talentcompetency: 'TalentCompetency',
  talenttrain: 'TalentTrain',
  talentexit: 'TalentExit',
  talentperformance: 'TalentPerformance',
  succession: 'Succession',
};

/**
 * Client-portal "Request evaluation" — mirrored 4-step wizard
 * (Sprint Preparo Phase 2, founder-approved flow):
 *   1 Suite → 2 Collaborators → 3 Employment Information (read-only,
 *   Canonical Employee Resolver) → 4 Review & Submit.
 * Wires to POST /api/client/evaluation-requests (contract unchanged).
 */
export function ClientEvaluationRequestDialog({ open, onOpenChange, onCreated, defaultSuite = '' }) {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || '').toLowerCase().startsWith('es');
  // Founder UAT — when opened from inside a suite the Suite step is skipped
  // and the suite shows as a locked chip.
  const locked = !!defaultSuite;
  const minStep = locked ? 1 : 0;

  const [step, setStep] = useState(minStep);
  const [bootLoading, setBootLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [company, setCompany] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [resolving, setResolving] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  const [suiteCode, setSuiteCode] = useState(defaultSuite);
  const [collaboratorIds, setCollaboratorIds] = useState([]);
  const [urgency, setUrgency] = useState('medium');
  const [notes, setNotes] = useState('');
  // Founder UAT TT-001 — TalentTrain business-context fields.
  const EMPTY_TRAINING = { objective: '', competencies: '', gap: '', outcome: '', type: '', justification: '' };
  const [training, setTraining] = useState(EMPTY_TRAINING);

  // TalentTrain gets an extra "Training details" step before review.
  const steps = useMemo(() => (suiteCode === 'talenttrain'
    ? ['suite', 'collaborators', 'employment', 'training', 'review']
    : STEPS), [suiteCode]);
  const cur = steps[step];

  const reset = () => {
    setStep(minStep); setSuiteCode(defaultSuite); setCollaboratorIds([]);
    setResolved([]); setExpandedCard(null); setUrgency('medium'); setNotes('');
    setTraining(EMPTY_TRAINING);
  };

  useEffect(() => {
    if (open && defaultSuite) setSuiteCode(defaultSuite);
  }, [open, defaultSuite]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setBootLoading(true);
    Promise.all([clientApi.getMyCompany(), clientApi.listCollaborators()])
      .then(([co, users]) => {
        if (!alive) return;
        setCompany(co || null);
        setCollaborators(users || []);
      })
      .catch((e) => toast.error(t('pages.clientRequests.loadFailed'), { description: formatApiError(e) }))
      .finally(() => { if (alive) setBootLoading(false); });
    return () => { alive = false; };
  }, [open, t]);

  // Step 3 — canonical employment records (read-only, resolver-sourced).
  useEffect(() => {
    if (steps[step] !== 'employment' || collaboratorIds.length === 0) return;
    setResolving(true);
    clientApi.resolveCollaborators(collaboratorIds)
      .then(setResolved)
      .catch(() => setResolved([]))
      .finally(() => setResolving(false));
  }, [step, steps, collaboratorIds]);

  const activeSuites = useMemo(
    () => (company?.suite_codes || []).filter((c) => !EXCLUDED_SUITES.includes(c)),
    [company],
  );

  const toggleCollaborator = (id) => {
    setCollaboratorIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const close = () => {
    if (submitting) return;
    onOpenChange(false);
    setTimeout(reset, 200);
  };

  // Shared employment values derived silently from canonical records —
  // keeps the POST contract identical to the pre-wizard implementation.
  const shared = (vals) => {
    const cleaned = vals.map((v) => (v || '').trim());
    if (cleaned.length === 0 || cleaned.some((v) => !v)) return null;
    const uniq = [...new Set(cleaned)];
    return uniq.length === 1 ? uniq[0] : null;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const body = {
        suite_code: suiteCode,
        collaborator_ids: collaboratorIds,
        position: shared(resolved.map((r) => r.position)),
        department: shared(resolved.map((r) => (isES ? r.department?.name_es : r.department?.name_en))),
        urgency,
        notes: notes || null,
      };
      if (suiteCode === 'talenttrain') {
        body.training_objective = training.objective.trim();
        body.training_competencies = training.competencies.trim() || null;
        body.training_gap = training.gap.trim() || null;
        body.training_expected_outcome = training.outcome.trim() || null;
        body.training_type = training.type || null;
        body.training_justification = training.justification.trim() || null;
      }
      const created = await clientApi.createEvaluationRequest(body);
      toast.success(t('pages.clientRequests.submittedToast'), {
        description: t('pages.clientRequests.submittedDesc'),
      });
      onCreated && onCreated(created);
      close();
    } catch (e) {
      toast.error(t('pages.clientRequests.submitFailed'), { description: formatApiError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = cur === 'suite' ? !!suiteCode
    : cur === 'collaborators' ? collaboratorIds.length > 0
    : cur === 'training' ? training.objective.trim().length > 0
    : true;
  const stepTitles = {
    suite: t('pages.clientRequests.stepSuite', { defaultValue: 'Suite' }),
    collaborators: t('pages.clientRequests.stepCollaborators', { defaultValue: 'Collaborators' }),
    employment: t('pages.clientRequests.stepEmployment', { defaultValue: 'Employment information' }),
    training: t('pages.clientRequests.stepTraining', { defaultValue: 'Training details' }),
    review: t('pages.clientRequests.stepReview', { defaultValue: 'Review & submit' }),
  };
  const deptLabel = (r) => (r.department ? ((isES ? r.department.name_es : r.department.name_en) || r.department.name) : null);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-3xl w-[min(92vw,52rem)]" data-testid="client-eval-request-dialog">
        <DialogHeader>
          <DialogTitle>{t('pages.clientRequests.requestEvaluation')}</DialogTitle>
          <DialogDescription>{t('pages.clientRequests.requestDesc')}</DialogDescription>
        </DialogHeader>

        {/* Locked suite chip (opened from inside a suite) */}
        {locked && (
          <div className="flex items-center gap-2 -mt-1" data-testid="cer-locked-suite">
            <span className="text-sm text-muted-foreground">{t('pages.clientRequests.suite')}:</span>
            <span className="inline-flex items-center gap-1 text-xs font-semibold bg-primary/10 text-primary border border-primary/25 rounded-full px-2.5 py-0.5">
              <Check className="h-3 w-3" /> {SUITE_NAMES[defaultSuite] || defaultSuite}
            </span>
          </div>
        )}

        {/* Stepper */}
        <div className="flex items-center gap-1.5" data-testid="cer-stepper">
          {steps.slice(minStep).map((s, idx) => {
            const i = idx + minStep;
            return (
            <React.Fragment key={s}>
              <div className="flex items-center gap-1.5" data-testid={`cer-step-ind-${idx + 1}`}>
                <span className={`h-6 w-6 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${
                  i < step ? 'bg-success text-success-foreground' : i === step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  {i < step ? <Check className="h-3.5 w-3.5" /> : idx + 1}
                </span>
                <span className={`text-sm hidden sm:inline ${i === step ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{stepTitles[s]}</span>
              </div>
              {i < steps.length - 1 && <div className="flex-1 h-px bg-border" />}
            </React.Fragment>
            );
          })}
        </div>

        {bootLoading ? (
          <div className="py-12 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
          </div>
        ) : (
          <div className="py-1 min-h-52">
            {step === 0 && (
              <div className="space-y-1.5" data-testid="cer-step-suite">
                <Label>{t('pages.clientRequests.suite')}</Label>
                <Select value={suiteCode} onValueChange={setSuiteCode}>
                  <SelectTrigger data-testid="cer-suite">
                    <SelectValue placeholder={t('pages.clientRequests.suitePh')} />
                  </SelectTrigger>
                  <SelectContent>
                    {activeSuites.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        {t('pages.clientRequests.suiteEmpty')}
                      </div>
                    ) : activeSuites.map((code) => (
                      <SelectItem key={code} value={code} data-testid={`cer-suite-${code}`}>
                        {SUITE_NAMES[code] || code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {suiteCode === 'talenttrain' && (
                  <p className="text-sm text-muted-foreground flex items-start gap-1.5 pt-1" data-testid="cer-talenttrain-hint">
                    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    {t('pages.clientRequests.talenttrainModeHint', { defaultValue: 'La modalidad específica de TalentTrain será definida junto con el equipo CTH durante la configuración.' })}
                  </p>
                )}
              </div>
            )}

            {cur === 'collaborators' && (
              <div className="space-y-1.5" data-testid="cer-step-collaborators">
                <div className="flex items-center justify-between">
                  <Label>{t('pages.clientRequests.collaborators')}</Label>
                  {collaboratorIds.length > 0 && (
                    <span className="text-sm text-muted-foreground tabular-nums">
                      {t('pages.clientRequests.selected', { n: collaboratorIds.length })}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{t('pages.clientRequests.collaboratorsHint')}</p>
                <div className="rounded-lg border border-border max-h-52 overflow-y-auto divide-y divide-border" data-testid="cer-collaborators">
                  {collaborators.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground text-center">
                      {t('pages.clientRequests.collaboratorsEmpty')}
                    </div>
                  ) : collaborators.map((u) => (
                    <label key={u.id} htmlFor={`cer-c-${u.id}`}
                           className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40 cursor-pointer">
                      <Checkbox
                        id={`cer-c-${u.id}`}
                        checked={collaboratorIds.includes(u.id)}
                        onCheckedChange={() => toggleCollaborator(u.id)}
                        data-testid={`cer-c-${u.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{u.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {cur === 'employment' && (
              <div className="space-y-2" data-testid="cer-step-employment">
                <p className="text-sm text-muted-foreground flex items-start gap-1.5">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  {t('pages.clientRequests.employmentHint', { defaultValue: 'These values come from each employee profile and cannot be edited here. If something is incorrect, update the collaborator profile first.' })}
                </p>
                {resolving ? (
                  <div className="py-8 flex items-center justify-center text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('common.loading')}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {resolved.map((r) => {
                      const expanded = expandedCard === r.id || resolved.length === 1;
                      return (
                        <div key={r.id} className="rounded-lg border border-border" data-testid={`cer-emp-card-${r.id}`}>
                          <button type="button"
                                  onClick={() => setExpandedCard(expandedCard === r.id ? null : r.id)}
                                  className="w-full flex items-center justify-between px-3 py-2 text-left"
                                  data-testid={`cer-emp-toggle-${r.id}`}>
                            <span className="text-sm font-medium truncate">{r.full_name}</span>
                            {resolved.length > 1 && (expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />)}
                          </button>
                          {expanded && (
                            <div className="px-3 pb-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid={`cer-emp-detail-${r.id}`}>
                              <span className="text-muted-foreground">{t('pages.clientRequests.position')}</span>
                              <span className="font-medium">{r.position || '—'}</span>
                              <span className="text-muted-foreground">{t('pages.clientRequests.department')}</span>
                              <span className="font-medium">{deptLabel(r) || '—'}</span>
                              {r.employment_status && (
                                <>
                                  <span className="text-muted-foreground">{t('pages.clientRequests.employmentStatus', { defaultValue: 'Employment status' })}</span>
                                  <span className="font-medium capitalize">{r.employment_status}</span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <a href="/client/employees" target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                   data-testid="cer-edit-profile-link">
                  {t('pages.clientRequests.editProfileLink', { defaultValue: 'Edit collaborator profile' })}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}

            {/* Founder UAT TT-001 — TalentTrain business context */}
            {cur === 'training' && (
              <div className="space-y-3 max-h-72 overflow-y-auto pr-1" data-testid="cer-step-training">
                <div className="space-y-1">
                  <Label htmlFor="cer-tr-objective">
                    {t('pages.clientRequests.training.objective', { defaultValue: 'Training objective' })} <span className="text-destructive">*</span>
                  </Label>
                  <Textarea id="cer-tr-objective" rows={2} value={training.objective}
                            onChange={(e) => setTraining((d) => ({ ...d, objective: e.target.value }))}
                            placeholder={t('pages.clientRequests.training.objectivePh', { defaultValue: 'What should this training achieve?' })}
                            data-testid="cer-training-objective" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="cer-tr-competencies">{t('pages.clientRequests.training.competencies', { defaultValue: 'Competencies to strengthen' })}</Label>
                    <Textarea id="cer-tr-competencies" rows={2} value={training.competencies}
                              onChange={(e) => setTraining((d) => ({ ...d, competencies: e.target.value }))}
                              placeholder={t('pages.clientRequests.training.competenciesPh', { defaultValue: 'e.g. leadership, communication…' })}
                              data-testid="cer-training-competencies" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="cer-tr-gap">{t('pages.clientRequests.training.gap', { defaultValue: 'Observed performance gap' })}</Label>
                    <Textarea id="cer-tr-gap" rows={2} value={training.gap}
                              onChange={(e) => setTraining((d) => ({ ...d, gap: e.target.value }))}
                              placeholder={t('pages.clientRequests.training.gapPh', { defaultValue: 'What have you observed?' })}
                              data-testid="cer-training-gap" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="cer-tr-outcome">{t('pages.clientRequests.training.outcome', { defaultValue: 'Expected business outcome' })}</Label>
                    <Textarea id="cer-tr-outcome" rows={2} value={training.outcome}
                              onChange={(e) => setTraining((d) => ({ ...d, outcome: e.target.value }))}
                              placeholder={t('pages.clientRequests.training.outcomePh', { defaultValue: 'What result do you expect?' })}
                              data-testid="cer-training-outcome" />
                  </div>
                  <div className="space-y-1">
                    <Label>{t('pages.clientRequests.training.type', { defaultValue: 'Preferred training type' })}</Label>
                    <Select value={training.type} onValueChange={(v) => setTraining((d) => ({ ...d, type: v }))}>
                      <SelectTrigger data-testid="cer-training-type">
                        <SelectValue placeholder={t('pages.clientRequests.training.typePh', { defaultValue: 'Select (optional)…' })} />
                      </SelectTrigger>
                      <SelectContent>
                        {['course', 'workshop', 'mentoring', 'on_the_job', 'certification', 'other'].map((v) => (
                          <SelectItem key={v} value={v} data-testid={`cer-training-type-${v}`}>
                            {t(`pages.clientRequests.trainingType.${v}`, { defaultValue: v })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="cer-tr-justification">{t('pages.clientRequests.training.justification', { defaultValue: 'Business justification' })}</Label>
                  <Textarea id="cer-tr-justification" rows={2} value={training.justification}
                            onChange={(e) => setTraining((d) => ({ ...d, justification: e.target.value }))}
                            placeholder={t('pages.clientRequests.training.justificationPh', { defaultValue: 'Why is this training needed now?' })}
                            data-testid="cer-training-justification" />
                </div>
              </div>
            )}

            {cur === 'review' && (
              <div className="space-y-4" data-testid="cer-step-review">
                <div className="rounded-lg border border-border bg-muted/30 p-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs" data-testid="cer-review-summary">
                  <span className="text-muted-foreground">{t('pages.clientRequests.suite')}</span>
                  <span className="font-medium">{SUITE_NAMES[suiteCode] || suiteCode}</span>
                  <span className="text-muted-foreground">{t('pages.clientRequests.collaborators')}</span>
                  <span className="font-medium">{resolved.map((r) => r.full_name).join(', ') || collaboratorIds.length}</span>
                  {suiteCode === 'talenttrain' && training.objective.trim() && (
                    <>
                      <span className="text-muted-foreground">{t('pages.clientRequests.training.objective', { defaultValue: 'Training objective' })}</span>
                      <span className="font-medium" data-testid="cer-review-training-objective">{training.objective}</span>
                    </>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>{t('pages.clientRequests.urgency')}</Label>
                  <Select value={urgency} onValueChange={setUrgency}>
                    <SelectTrigger data-testid="cer-urgency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">{t('pages.clientRequests.urgencyLow')}</SelectItem>
                      <SelectItem value="medium">{t('pages.clientRequests.urgencyMedium')}</SelectItem>
                      <SelectItem value="high">{t('pages.clientRequests.urgencyHigh')}</SelectItem>
                      <SelectItem value="urgent">{t('pages.clientRequests.urgencyUrgent')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t('pages.clientRequests.notes')}</Label>
                  <Textarea rows={3} data-testid="cer-notes" value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder={t('pages.clientRequests.notesPh')} />
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close} disabled={submitting} data-testid="cer-cancel">
            {t('pages.clientRequests.cancel')}
          </Button>
          {step > minStep && (
            <Button variant="outline" onClick={() => setStep(step - 1)} disabled={submitting} data-testid="cer-back">
              {t('common.back', { defaultValue: 'Back' })}
            </Button>
          )}
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext || bootLoading}
                    className="bg-gradient-primary hover:opacity-90" data-testid="cer-next">
              {t('common.next', { defaultValue: 'Next' })}
            </Button>
          ) : (
            <Button data-testid="cer-submit" onClick={handleSubmit}
                    disabled={submitting || bootLoading}
                    className="bg-gradient-primary hover:opacity-90">
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('pages.clientRequests.submitting')}</>
              ) : t('pages.clientRequests.submit')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ClientEvaluationRequestDialog;
